'use strict';
/* ==========================================================================
   core-local/coaster.js — the roller-coaster physics model (DESIGN.md §3) plus the
   paper/ink drawing helpers for tracks, carts, rulers, gauges and the energy
   chart.

   Model (pure, deterministic, no DOM; SI units, y up, ground at y = 0):
     Track  — a curve in metres built from hill control points [{x, h}]
              (cosine-smoothed, so every control point is a flat crest or
              valley at exactly h) plus optional vertical loops, resampled
              uniformly by arc length s. track.at(s) → {x, y, angle, kappa, phase}.
     Sim    — 1-D integration along s, precomputed at construction:
                a = −g·sinθ − sign(v)·μ·N          (N/m = g·cosθ, or g·cosθ + v²κ in loops)
              RK4, 0.5 ms fixed step, stored at 240 Hz. sim.sample(t)
              interpolates, so drawing is a pure function of time.
              Energy per sample: PE = m g y, KE = ½ m v², heat = ∫ μ N |v| dt,
              work = chain-lift work in, total = PE + KE + heat.
              Cheats (Spot the Mistake) bend the dynamics but NOT the books:
              sample.total is always the honest PE + KE + heat, so a cheat
              shows up as a total that grows or shrinks.

   Works in node (module.exports, for tools/energycheck.js) and in the page
   (window.Coaster). Drawing helpers use the engine.js globals (g, T, PAL,
   paperRect, ink, hand, ...) and are only touched when called.
   ========================================================================== */
(function (root) {
  const G = 10;             // m/s², "about ten"
  const DS = 0.05;          // track resample step (m)
  const TAU_ = Math.PI * 2;
  const clamp_ = (x, a, b) => Math.max(a, Math.min(b, x));

  /* ======================================================================
     Track
     ====================================================================== */
  /* opts: { hills: [{x, h, loop?: {r, drift}}], ds }
       hills   control points left→right; the curve passes through (x, h) with zero
               slope, cosine-eased between neighbours (so crests/valleys are exact).
       loop    at a control point: a vertical loop of radius r (height 2r) that starts
               and ends there, drifting `drift` metres forward so entry and exit don't
               overlap. Every later control point shifts right by `drift`.          */
  class Track {
    constructor({ hills, ds = DS } = {}) {
      if (!hills || hills.length < 2) throw new Error('Track needs at least two hills');
      this.hills = hills.map(p => ({ ...p }));
      // 1. dense raw polyline (x, y, loop phase)
      const raw = [];
      let shift = 0;
      const push = (x, y, ph) => {
        const l = raw[raw.length - 1];
        if (l && Math.hypot(x - l[0], y - l[1]) < 1e-9) return;
        raw.push([x, y, ph]);
      };
      for (let i = 0; i < hills.length; i++) {
        const a = hills[i];
        if (i > 0) {
          const p = hills[i - 1], x0 = p.x + shift, x1 = a.x + shift;
          const n = Math.max(2, Math.ceil((x1 - x0) / 0.02));
          for (let k = 1; k <= n; k++) {
            const u = k / n;
            push(x0 + (x1 - x0) * u, p.h + (a.h - p.h) * (1 - Math.cos(Math.PI * u)) / 2, NaN);
          }
        } else push(a.x, a.h, NaN);
        if (a.loop) {
          const r = a.loop.r, d = a.loop.drift ?? r * 0.8, xL = a.x + shift, yL = a.h;
          const n = Math.ceil(TAU_ * r / 0.02);
          for (let k = 1; k <= n; k++) {
            const f = k / n, ph = f * TAU_;
            push(xL + r * Math.sin(ph) + d * f, yL + r * (1 - Math.cos(ph)), k === n ? NaN : f);
          }
          a._loopX = xL; a._drift = d;
          shift += d;
        }
      }
      // 2. resample uniformly by arc length
      const cum = [0];
      for (let i = 1; i < raw.length; i++) cum.push(cum[i - 1] + Math.hypot(raw[i][0] - raw[i - 1][0], raw[i][1] - raw[i - 1][1]));
      const L = cum[cum.length - 1], N = Math.floor(L / ds) + 1;
      this.ds = ds; this.length = (N - 1) * ds; this.n = N;
      const X = this.X = new Float64Array(N), Y = this.Y = new Float64Array(N), PH = this.PH = new Float64Array(N);
      let j = 0;
      for (let i = 0; i < N; i++) {
        const s = i * ds;
        while (j < raw.length - 2 && cum[j + 1] < s) j++;
        const seg = cum[j + 1] - cum[j], u = seg > 0 ? (s - cum[j]) / seg : 0;
        X[i] = raw[j][0] + (raw[j + 1][0] - raw[j][0]) * u;
        Y[i] = raw[j][1] + (raw[j + 1][1] - raw[j][1]) * u;
        const pa = raw[j][2], pb = raw[j + 1][2];
        PH[i] = isNaN(pa) && isNaN(pb) ? NaN : isNaN(pa) ? (u > 0.5 ? pb : NaN) : isNaN(pb) ? (u < 0.5 ? pa : NaN) : pa + (pb - pa) * u;
      }
      // 3. tangent angle (unwrapped, so a loop turns smoothly through 2π) and curvature dθ/ds
      const A = this.A = new Float64Array(N), K = this.K = new Float64Array(N);
      for (let i = 0; i < N; i++) {
        const a = Math.max(0, i - 1), b = Math.min(N - 1, i + 1);
        let ang = Math.atan2(Y[b] - Y[a], X[b] - X[a]);
        if (i > 0) { while (ang - A[i - 1] > Math.PI) ang -= TAU_; while (ang - A[i - 1] < -Math.PI) ang += TAU_; }
        A[i] = ang;
      }
      for (let i = 0; i < N; i++) {
        const a = Math.max(0, i - 2), b = Math.min(N - 1, i + 2);
        K[i] = (A[b] - A[a]) / ((b - a) * ds);
      }
      // exact slope of the piecewise-linear height table: gravity uses this so the
      // work done by gravity equals −Δ(m g y) exactly (no drift from the discretisation)
      const SL = this.SL = new Float64Array(N);
      for (let i = 0; i < N - 1; i++) SL[i] = (Y[i + 1] - Y[i]) / ds;
      SL[N - 1] = SL[N - 2];
      this.minX = Math.min(...X); this.maxX = Math.max(...X);
      this.maxY = Math.max(...Y); this.minY = Math.min(...Y);
    }

    /* point at arc length s → {s, x, y, angle (rad, y-up, CCW from +x), kappa (1/m), phase (0..1 in a loop, else NaN), slope} */
    at(s) {
      s = clamp_(s, 0, this.length);
      const f = s / this.ds, i = Math.min(this.n - 2, Math.floor(f)), u = f - i;
      const ph0 = this.PH[i], ph1 = this.PH[i + 1];
      return {
        s,
        x: this.X[i] + (this.X[i + 1] - this.X[i]) * u,
        y: this.Y[i] + (this.Y[i + 1] - this.Y[i]) * u,
        angle: this.A[i] + (this.A[i + 1] - this.A[i]) * u,
        kappa: this.K[i] + (this.K[i + 1] - this.K[i]) * u,
        phase: isNaN(ph0) || isNaN(ph1) ? (u < 0.5 ? ph0 : ph1) : ph0 + (ph1 - ph0) * u,
        slope: this.SL[i],
      };
    }
    y(s) { const f = clamp_(s, 0, this.length) / this.ds, i = Math.min(this.n - 2, Math.floor(f)), u = f - i; return this.Y[i] + (this.Y[i + 1] - this.Y[i]) * u; }
    x(s) { const f = clamp_(s, 0, this.length) / this.ds, i = Math.min(this.n - 2, Math.floor(f)), u = f - i; return this.X[i] + (this.X[i + 1] - this.X[i]) * u; }

    /* first arc length where the track reaches x (walking forward from sFrom) */
    sAtX(x, sFrom = 0) {
      for (let i = Math.max(0, Math.floor(sFrom / this.ds)); i < this.n - 1; i++) {
        const a = this.X[i], b = this.X[i + 1];
        if ((a - x) * (b - x) <= 0 && a !== b) return (i + (x - a) / (b - a)) * this.ds;
      }
      return x <= this.X[0] ? 0 : this.length;
    }
    /* the x of control point i after loop drift is applied */
    hillX(i) { let sh = 0; for (let k = 0; k < i; k++) if (this.hills[k].loop) sh += this.hills[k]._drift; return this.hills[i].x + sh; }
  }

  /* ======================================================================
     Sim
     ====================================================================== */
  /* opts: { track, m = 500, g = 10, mu = 0, x0 | s0, v0 = 0, dt = 0.0005, tMax = 20, hz = 240,
             chain:  { x0, x1, speed = 2, hold = false }   constant-speed lift; its work is booked as sample.work
                     (hold: the cart is parked at rest at the top of the lift, its KE handed back to the chain)
             brakes: { x0, x1, mu = 0.6 }    extra friction there (honest: booked as heat)
             cheat:  { type, ... }           see CHEATS below
             settleV = 0.05 }
     A cart that runs off either end of the track hits a buffer: v = 0 and the lost KE is
     booked as heat (sim.hitEnd = true; tracks.js configs never do this). */
  class Sim {
    constructor(o) {
      const tr = this.track = o.track;
      this.m = o.m ?? 500; this.g = o.g ?? G; this.mu = o.mu ?? 0;
      this.dt = o.dt ?? 0.0005; this.tMax = o.tMax ?? 20; this.hz = o.hz ?? 240;
      this.settleV = o.settleV ?? 0.05;
      this.cheat = o.cheat || null;
      const sx = (x) => tr.sAtX(x);
      this.chain = o.chain ? { s0: o.chain.s0 ?? sx(o.chain.x0), s1: o.chain.s1 ?? sx(o.chain.x1), speed: o.chain.speed ?? 2, hold: !!o.chain.hold } : null;
      this.brakes = o.brakes ? { s0: o.brakes.s0 ?? sx(o.brakes.x0), s1: o.brakes.s1 ?? sx(o.brakes.x1), mu: o.brakes.mu ?? 0.6 } : null;
      const c = this.cheat;
      if (c && (c.x0 !== undefined || c.x1 !== undefined)) { c.s0 = c.s0 ?? sx(c.x0 ?? -1e9); c.s1 = c.s1 ?? sx(c.x1 ?? 1e9); }
      this.s0 = o.s0 ?? (o.x0 !== undefined ? sx(o.x0) : 0);
      this.v0 = o.v0 ?? 0;
      this.h0 = o.h0 ?? null;
      if (o.h0 !== undefined) this.release(o.h0, o.v0);
      this.hitEnd = false;
      this.run();
    }

    /* h0 = the release energy height: the cart leaves with exactly m·g·h0 (PE + KE), so the
       chart's TOTAL reads the script's number (500 kg × 10 × 20 m = 100,000 J), not 100,062 J.
         v0 given   → a nudge off the crest: start a hair down the lip, where y = h0 − v0²/2g
                      (the nudge's KE is paid for by those few millimetres of height);
         v0 omitted → released just past the lip (friction tracks, where the slope must beat μ):
                      the cart gets the speed that makes up the height it already lost. */
    release(h0, v0) {
      const tr = this.track, g = this.g;
      const fit = s => { this.s0 = s; this.v0 = Math.sqrt(Math.max(0, 2 * g * (h0 - tr.y(s)))); };
      if (v0 === undefined || v0 === null) return fit(this.s0);
      const target = h0 - v0 * v0 / (2 * g);
      if (tr.y(this.s0) <= target) return fit(this.s0);
      const step = tr.ds / 4;
      let a = this.s0, b = a;
      while (b < tr.length && tr.y(b) > target) { a = b; b += step; }
      for (let i = 0; i < 60; i++) { const mid = (a + b) / 2; if (tr.y(mid) > target) a = mid; else b = mid; }
      fit(b);                                            // y(b) = target to ~1e-12 m, so v0 ≈ the nudge
    }

    /* friction coefficient at s: track μ + brakes (honest, booked as heat) */
    muAt(s) {
      let mu = this.mu;
      const b = this.brakes; if (b && s >= b.s0 && s <= b.s1) mu += b.mu;
      return mu;
    }
    inCheat(s) { const c = this.cheat; return c && (c.s0 === undefined || (s >= c.s0 && s <= c.s1)); }

    /* derivatives of (s, v, heat/m, leak/m) at state (s, v).
       heat = honest friction heat; leak = energy a cheat removes without booking it. */
    deriv(s, v) {
      const tr = this.track, f = clamp_(s, 0, tr.length) / tr.ds;
      const i = Math.min(tr.n - 2, Math.floor(f)), u = f - i;
      const slope = tr.SL[i];                                      // dy/ds = sinθ
      const cos = Math.sqrt(Math.max(0, 1 - slope * slope)) * (Math.cos(tr.A[i] + (tr.A[i + 1] - tr.A[i]) * u) < 0 ? -1 : 1);
      const inLoop = !isNaN(tr.PH[i]);
      const kappa = tr.K[i] + (tr.K[i + 1] - tr.K[i]) * u;
      let gs = this.g;                                            // gravity felt along the track
      const c = this.cheat, on = c && this.inCheat(s);
      if (on && c.type === 'extraEnergy') gs *= c.scale ?? 0.6;
      if (on && c.type === 'massMatters') gs *= c.factor ?? 2;
      if (on && c.type === 'wrongGauge') gs *= Math.pow(c.factor ?? 2, 2);
      let aG = -gs * slope;
      if (on && c.type === 'loopBoost' && inLoop) aG = -aG;       // speeds up as it climbs the loop
      const N = Math.max(0, inLoop ? this.g * cos + v * v * kappa : this.g * cos);   // normal force per kg
      const mu = this.muAt(s), sg = v > 0 ? 1 : v < 0 ? -1 : 0;
      let aF = -sg * mu * N;
      let leak = 0;
      if (on && c.type === 'phantomFriction') { const pm = c.mu ?? 0.25; aF -= sg * pm * this.g; leak = pm * this.g * Math.abs(v); }
      return [v, aG + aF, mu * N * Math.abs(v), leak, aG, mu * N + (on && c.type === 'phantomFriction' ? (c.mu ?? 0.25) * this.g : 0)];
    }

    run() {
      const tr = this.track, dt = this.dt, every = Math.max(1, Math.round(1 / (this.hz * dt)));
      const nOut = Math.ceil(this.tMax / (dt * every)) + 1;
      const S = this._S = new Float64Array(nOut), V = this._V = new Float64Array(nOut);
      const Q = this._Q = new Float64Array(nOut), Wk = this._W = new Float64Array(nOut), Lk = this._L = new Float64Array(nOut);
      const HOLD = this._H = new Uint8Array(nOut);
      this.sdt = dt * every;
      let s = this.s0, v = this.v0, q = 0, w = 0, lk = 0, held = false;
      this.tSettle = null; this.turns = [];
      const ch = this.chain;
      for (let k = 0; k < nOut; k++) {
        S[k] = s; V[k] = v; Q[k] = q; Wk[k] = w; Lk[k] = lk; HOLD[k] = held ? 1 : 0;
        if (k === nOut - 1) break;
        for (let j = 0; j < every; j++) {
          if (held) continue;
          const t = (k * every + j) * dt;
          // chain lift: locked to the chain while inside it and slower than it
          if (ch && s >= ch.s0 && s < ch.s1 && v <= ch.speed + 1e-9) {
            const e0 = tr.y(s) * this.g + 0.5 * v * v + q;
            const d = this.deriv(s, ch.speed);
            q += d[2] * dt;                     // friction on the lift still makes heat…
            v = ch.speed; s += v * dt;
            w += tr.y(s) * this.g + 0.5 * v * v + q - e0;   // …and the chain pays for all of it
            if (ch.hold && s >= ch.s1) { w -= 0.5 * v * v; v = 0; held = true; this.tTop = (k + 1) * this.sdt; }   // parked at the top: the lift's brake takes the KE back
            continue;
          }
          // RK4 on (s, v, q, leak)
          const d1 = this.deriv(s, v);
          const d2 = this.deriv(s + d1[0] * dt / 2, v + d1[1] * dt / 2);
          const d3 = this.deriv(s + d2[0] * dt / 2, v + d2[1] * dt / 2);
          const d4 = this.deriv(s + d3[0] * dt, v + d3[1] * dt);
          const vPrev = v;
          s += dt / 6 * (d1[0] + 2 * d2[0] + 2 * d3[0] + d4[0]);
          v += dt / 6 * (d1[1] + 2 * d2[1] + 2 * d3[1] + d4[1]);
          q += dt / 6 * (d1[2] + 2 * d2[2] + 2 * d3[2] + d4[2]);
          lk += dt / 6 * (d1[3] + 2 * d2[3] + 2 * d3[3] + d4[3]);
          // ends of the track: buffer stop (KE → heat, booked honestly)
          if (s <= 0 || s >= tr.length) {
            s = clamp_(s, 0, tr.length); q += 0.5 * v * v; v = 0; this.hitEnd = true; held = true;
            this.tSettle = t + dt; continue;
          }
          // turnaround (v crossed 0): roll back, unless static friction can hold it here
          const turned = (vPrev > 0 && v <= 0) || (vPrev < 0 && v >= 0);
          if (turned) this.turns.push({ t: t + dt, s, y: tr.y(s) });
          const d = this.deriv(s, 0);
          const canHold = Math.abs(d[4]) <= d[5] + 1e-9;
          const fr = this.muAt(s) > 0 || (this.cheat && this.cheat.type === 'phantomFriction' && this.inCheat(s));
          if (fr && canHold && (turned || Math.abs(v) < this.settleV)) {
            // settle: the remaining sliver of KE goes into heat (friction stops it), then hold
            q += 0.5 * v * v; v = 0; held = true; this.tSettle = t + dt;
          }
        }
      }
      this.n = nOut;
      this.duration = (nOut - 1) * this.sdt;
      const e0 = this.energyAt(0);
      this.total0 = e0.total;
    }

    /* exact stored sample k → energies (J) */
    energyAt(k) {
      const m = this.m, s = this._S[k], v = this._V[k];
      const PE = m * this.g * this.track.y(s), KE = 0.5 * m * v * v, heat = m * this._Q[k];
      return { PE, KE, heat, work: m * this._W[k], leak: m * this._L[k], total: PE + KE + heat };
    }

    /* interpolated state at time t (clamped to [0, duration]) — draw from this, it is a pure function of t */
    sample(t) {
      const f = clamp_(t / this.sdt, 0, this.n - 1), k = Math.min(this.n - 2, Math.floor(f)), u = f - k;
      const L = (A) => A[k] + (A[k + 1] - A[k]) * u;
      const s = L(this._S), v = L(this._V), m = this.m;
      const p = this.track.at(s);
      const PE = m * this.g * p.y, KE = 0.5 * m * v * v, heat = m * L(this._Q);
      return {
        t: clamp_(t, 0, this.duration), s, v, speed: Math.abs(v), x: p.x, y: p.y, angle: p.angle, kappa: p.kappa, phase: p.phase,
        PE, KE, heat, work: m * L(this._W), total: PE + KE + heat, total0: this.total0,
        settled: this._H[u < 0.5 ? k : k + 1] === 1, m,
      };
    }

    /* first stored time ≥ tFrom where pred(sample) is true (null if never) */
    when(pred, tFrom = 0) {
      for (let k = Math.max(0, Math.floor(tFrom / this.sdt)); k < this.n; k++) if (pred(this.sample(k * this.sdt), k)) return k * this.sdt;
      return null;
    }
    /* first time the cart passes x (moving forward) */
    timeAtX(x, tFrom = 0) {
      for (let k = Math.max(1, Math.floor(tFrom / this.sdt)); k < this.n; k++) {
        const a = this.track.x(this._S[k - 1]), b = this.track.x(this._S[k]);
        if (a < x && b >= x) return (k - 1 + (x - a) / (b - a)) * this.sdt;
      }
      return null;
    }
    maxY(t0 = 0, t1 = this.duration) {
      let best = -Infinity;
      for (let k = Math.floor(t0 / this.sdt); k <= Math.min(this.n - 1, Math.ceil(t1 / this.sdt)); k++) best = Math.max(best, this.track.y(this._S[k]));
      return best;
    }
  }

  /* ======================================================================
     Drawing helpers (browser only; engine.js globals).
     A view maps metres (y up) to logical pixels (y down):  X = ox + x·k,  Y = oy − y·k.
     All helpers draw through `g`, so they work inside a scene's camera() transform.
     ====================================================================== */
  function view(ox, oy, k = 22) {
    return { ox, oy, k, X: x => ox + x * k, Y: y => oy - y * k, P: (x, y) => [ox + x * k, oy - y * k] };
  }
  const kmh = v => v * 3.6;
  /* display rounding: nearest 100 J from 10,000 J up, 10 J from 1,000 J, else 1 J */
  const unitJ = (j) => { const a = Math.abs(j); return a >= 10000 ? 100 : a >= 1000 ? 10 : 1; };
  const fmtJ = (j, unit = unitJ(j)) => (Math.round(j / unit) * unit).toLocaleString('en-US') + ' J';
  /* the chart's numbers: TOTAL rounded as fmtJ does, and PE / KE / heat rounded to the same unit
     with the largest-remainder rule so the bars' labels add up to the TOTAL's label exactly
     (15,850 + 84,150 shows as 15,800 + 84,200 = 100,000, never 15,900 + 84,200 = 100,000).
     → { PE, KE, heat, total, unit } in J (display values). */
  function displayJ(e) {
    const unit = unitJ(e.total), T = Math.round(e.total / unit);
    const parts = [e.PE, e.KE, e.heat || 0].map(v => Math.max(0, v) / unit);
    const fl = parts.map(Math.floor);
    let left = T - fl.reduce((a, b) => a + b, 0);
    const order = parts.map((v, i) => [v - fl[i], i]).sort((a, b) => b[0] - a[0]);
    for (let k = 0; left > 0 && k < 3 * 3; k++, left--) fl[order[k % 3][1]]++;
    for (let k = 0; left < 0 && k < 3 * 3; k++, left++) { const i = order[2 - (k % 3)][1]; if (fl[i] > 0) fl[i]--; }
    return { PE: fl[0] * unit, KE: fl[1] * unit, heat: fl[2] * unit, total: T * unit, unit };
  }

  /* a boiling polyline drawn as one path (cheaper than ink() for long tracks) */
  function jitterPath(pts, seed, amp) {
    g.beginPath();
    pts.forEach((p, i) => { const x = p[0] + jit(seed + i * 7, amp), y = p[1] + jit(seed + i * 7 + 3, amp); i ? g.lineTo(x, y) : g.moveTo(x, y); });
  }

  /* track in paper/ink style: wooden posts down to the ground, kraft rails, ink cross-ties.
     opts: { s0 = 0, s1 = track.length, prog = 1 (draw-on fraction), ground = 0 (m), posts = true,
             postEvery = 3.2 (m of x), tieEvery = 1.1 (m of s), rail = 0.55 (m, rail band thickness),
             color = PAL.kraft, seed = 1, alpha = 1 } */
  function drawTrack(track, vw, o = {}) {
    const k = vw.k, seed = o.seed ?? 1, s0 = o.s0 ?? 0;
    const s1 = s0 + ((o.s1 ?? track.length) - s0) * clamp_(o.prog ?? 1, 0, 1);
    if (s1 <= s0) return;
    const ground = o.ground ?? 0, railW = Math.max(6, (o.rail ?? 0.55) * k);
    g.save(); g.globalAlpha *= o.alpha ?? 1;
    // 1. support posts (skip the loop: it stands on its own bottom)
    if (o.posts !== false) {
      const every = o.postEvery ?? 3.2;
      let lastX = -1e9; const tops = [];
      for (let s = s0; s <= s1; s += track.ds * 4) {
        const p = track.at(s);
        if (!isNaN(p.phase) || p.y - ground < 0.4) continue;
        if (p.x - lastX >= every) { tops.push(p); lastX = p.x; }
      }
      const pw = Math.max(5, 0.28 * k);
      tops.forEach((p, i) => {
        const X = vw.X(p.x), Yt = vw.Y(p.y) + railW * 0.4, Yb = vw.Y(ground);
        g.fillStyle = paperTile('#b98a5a', 6, 'tex_kraft');
        g.fillRect(X - pw / 2, Yt, pw, Yb - Yt);
        g.lineWidth = 1.6; g.strokeStyle = 'rgba(43,36,32,0.55)';
        jitterPath([[X - pw / 2, Yt], [X - pw / 2, Yb]], seed * 31 + i, 0.6); g.stroke();
        jitterPath([[X + pw / 2, Yt], [X + pw / 2, Yb]], seed * 31 + i + 500, 0.6); g.stroke();
        // cross-bracing to the next post (short posts don't need it)
        const q = tops[i + 1];
        if (q && Math.min(p.y, q.y) - ground > 2.5) {
          const X2 = vw.X(q.x), hTop = Math.min(vw.Y(p.y), vw.Y(q.y)) + railW, Yb2 = Yb - 4;
          g.lineWidth = 1.4; g.strokeStyle = 'rgba(90,64,40,0.5)';
          jitterPath([[X, hTop], [X2, Yb2]], seed * 53 + i, 0.8); g.stroke();
          jitterPath([[X2, hTop], [X, Yb2]], seed * 59 + i, 0.8); g.stroke();
        }
      });
    }
    // 2. rails: ink underlay with a paper shadow, kraft band, light top edge
    const stride = Math.max(1, Math.round(5 / (k * track.ds)));
    const pts = [];
    for (let s = s0; s < s1; s += track.ds * stride) { const p = track.at(s); pts.push([vw.X(p.x), vw.Y(p.y)]); }
    const pe = track.at(s1); pts.push([vw.X(pe.x), vw.Y(pe.y)]);
    g.lineJoin = 'round'; g.lineCap = 'round';
    // the ink underlay's paper shadow: pre-baked through the engine's low-resolution path (no per-frame shadowBlur)
    const bb = ptsBox(pts), m = railW / 2 + 6;
    const cast = castShadow(bb.x - m, bb.y - m, bb.w + 2 * m, bb.h + 2 * m, 0.9, () => {
      g.lineJoin = 'round'; g.lineCap = 'round'; g.globalAlpha = 0.85; g.lineWidth = railW + 5; jitterPath(pts, seed * 11, 0.7); g.stroke();
    });
    if (!cast) shadowOn(0.9);
    g.strokeStyle = 'rgba(43,36,32,0.85)'; g.lineWidth = railW + 5; jitterPath(pts, seed * 11, 0.7); g.stroke(); shadowOff();
    g.strokeStyle = paperTile(o.color ?? PAL.kraft, 3, 'tex_kraft'); g.lineWidth = railW; jitterPath(pts, seed * 11, 0.7); g.stroke();
    g.strokeStyle = 'rgba(255,245,220,0.45)'; g.lineWidth = Math.max(1.5, railW * 0.18);
    g.save(); g.translate(0, -railW * 0.28); jitterPath(pts, seed * 11, 0.7); g.stroke(); g.restore();
    // 3. cross-ties: short ink ticks across the rail band
    const te = Math.max(o.tieEvery ?? 1.1, 9 / k), half = railW * 0.5 + 3;   // never closer than 9 px
    g.beginPath(); let i = 0;
    for (let s = Math.ceil(s0 / te) * te; s <= s1; s += te, i++) {
      const p = track.at(s), X = vw.X(p.x), Y = vw.Y(p.y), nx = -Math.sin(p.angle), ny = -Math.cos(p.angle);   // screen normal
      g.moveTo(X + nx * half + jit(seed + i, 0.8), Y + ny * half + jit(seed + i + 1, 0.8));
      g.lineTo(X - nx * half + jit(seed + i + 2, 0.8), Y - ny * half + jit(seed + i + 3, 0.8));
    }
    g.strokeStyle = 'rgba(43,36,32,0.8)'; g.lineWidth = 2.2; g.stroke();
    g.restore();
  }

  /* the cart riding the track at arc length s (or at a sample {s}).
     opts: { h = 2.4 m·k (px), rider = image name|null, riderH (px), riderDx, riderDy (px, cart frame),
             glow = 0..1 (warm wheels), label = text on the side, seed = 5, color = PAL.red (placeholder),
             rail = 0.55 (m, match drawTrack) }  → returns {x, y, angle} of the cart centre (screen) */
  /* per-image fit of assets/img/cart.png (measured from the cut-out's ink): dy = shift down (× h) so the wheels,
     not the paper border, touch the rail; seatX / feetY = default rider placement (× cart width / × h) in the seat */
  const CART_FIT = { dy: 0.035, seatX: -0.1, feetY: 0.08 };
  function drawCart(track, vw, s, o = {}) {
    if (typeof s === 'object') s = s.s;
    const p = track.at(s), k = vw.k, h = o.h ?? 2.4 * k, seed = o.seed ?? 5;
    const railW = Math.max(6, (o.rail ?? 0.55) * k);
    const rot = -p.angle, nx = -Math.sin(p.angle), ny = -Math.cos(p.angle);
    const lift = h / 2 + railW / 2 - 2;
    const cx = vw.X(p.x) + nx * lift, cy = vw.Y(p.y) + ny * lift;
    const im = IMG.cart, w = im ? h * im.width / im.height : h * 1.7;
    g.save(); g.translate(cx, cy); g.rotate(rot);
    if (im) g.translate(0, CART_FIT.dy * h);
    // rider sits down inside the cart (drawn first, so the cart body hides the legs)
    if (o.rider) {
      const rh = o.riderH ?? h * 1.6, rx = o.riderDx ?? (im ? CART_FIT.seatX * w : -w * 0.05), ry = o.riderDy ?? (im ? CART_FIT.feetY * h : -h * 0.1);
      if (IMG[o.rider]) cut(o.rider, rx, ry, rh, { ax: 0.5, ay: 1, seed: seed + 3, depth: 0.8, flip: !!o.riderFlip });
      else { paperRect(rx, ry - rh * 0.35, rh * 0.42, rh * 0.7, PAL.navy, { seed: seed + 3, depth: 0.8 }); paperCircle(rx, ry - rh * 0.8, rh * 0.16, PAL.pink, { seed: seed + 4 }); }
    }
    if (im) cut('cart', 0, 0, h, { seed, depth: 1.1 });
    else {
      // placeholder paper cart: body, stripe, two wheels, ink outline
      paperRect(0, -h * 0.08, w, h * 0.72, o.color ?? PAL.red, { seed, depth: 1.1, outline: true });
      paperRect(0, -h * 0.12, w * 0.9, h * 0.12, PAL.mustard, { seed: seed + 1, depth: 0 });
      [-0.3, 0.3].forEach((fx, i) => paperCircle(w * fx, h * 0.33, h * 0.17, PAL.navy, { seed: seed + 7 + i, depth: 0.6 }));
    }
    if (o.label) hand(o.label, 0, -h * 0.02, h * 0.36, -1e6, { dur: 0.01, color: PAL.cream, seed: seed + 9, outline: 'rgba(43,36,32,0.6)' });
    const glow = clamp_(o.glow || 0, 0, 1);
    if (glow > 0) {
      [-0.3, 0.3].forEach((fx, i) => {
        const wx = w * fx, wy = h * 0.36, r = h * 0.42 * (0.7 + 0.3 * glow);
        const gr = g.createRadialGradient(wx, wy, 0, wx, wy, r);
        gr.addColorStop(0, `rgba(255,170,60,${0.85 * glow})`); gr.addColorStop(0.45, `rgba(239,107,74,${0.5 * glow})`); gr.addColorStop(1, 'rgba(239,107,74,0)');
        g.fillStyle = gr; g.beginPath(); g.arc(wx, wy, r, 0, TAU_); g.fill();
        // little heat squiggles rising off the wheel
        for (let j = 0; j < 2; j++) {
          const ph = (T * 0.8 + j * 0.5 + i * 0.25) % 1, sx0 = wx + (j - 0.5) * h * 0.2, sy0 = wy - h * 0.25 - ph * h * 0.6;
          const pts = []; for (let q = 0; q <= 6; q++) pts.push([sx0 + Math.sin(q * 1.3 + T * 6) * h * 0.05, sy0 - q * h * 0.05]);
          ink(pts, { w: 3, color: PAL.tomato, alpha: glow * (1 - ph), seed: seed + 20 + j + i * 3, step: 6, passes: 1 });
        }
      });
    }
    g.restore();
    return { x: cx, y: cy, angle: rot };
  }

  /* height ruler: a paper tape measure standing at world x, from y0 to y1 metres.
     opts: { step = 1, labelEvery = 5, mark = null (current height, m → red arrow + readout),
             side = 'left'|'right' (which side the labels go), seed = 3, t0 = 0 (write-on), w = 34 } */
  function drawRuler(vw, x, o = {}) {
    const y0 = o.y0 ?? 0, y1 = o.y1 ?? 20, step = o.step ?? 1, every = o.labelEvery ?? 5, seed = o.seed ?? 3;
    const X = vw.X(x), Ytop = vw.Y(y1) - 18, Ybot = vw.Y(y0), w = o.w ?? 34, dir = o.side === 'right' ? 1 : -1;
    paperRect(X, (Ytop + Ybot) / 2, w, Ybot - Ytop, PAL.mustard, { seed, depth: 0.9, boil: false });
    tape(X, Ytop + 6, 70, 0.2, undefined, seed + 1);
    g.beginPath();
    for (let y = y0, i = 0; y <= y1 + 1e-6; y += step, i++) {
      const Y = vw.Y(y), big = Math.abs(y / every - Math.round(y / every)) < 1e-6, L = big ? w * 0.7 : w * 0.38;
      g.moveTo(X - w / 2 + jit(seed + i, 0.4), Y); g.lineTo(X - w / 2 + L + jit(seed + i + 1, 0.4), Y);
    }
    g.strokeStyle = PAL.ink; g.lineWidth = 2.2; g.stroke();
    g.save(); g.font = "26px 'Caveat Brush'"; g.fillStyle = PAL.ink; g.textBaseline = 'middle'; g.textAlign = dir < 0 ? 'right' : 'left';
    for (let y = y0; y <= y1 + 1e-6; y += every) g.fillText(Math.round(y) + ' m', X + dir * (w / 2 + 8), vw.Y(y));
    g.restore();
    if (o.mark !== null && o.mark !== undefined) {
      const Y = vw.Y(clamp_(o.mark, y0 - 1, y1 + 3));
      paperArrow(X - dir * (w / 2 + 52), Y, 44, 16, PAL.red, { rot: dir < 0 ? 0 : Math.PI, seed: seed + 5, depth: 0.7 });
      hand(o.mark.toFixed(1) + ' m', X - dir * (w / 2 + 62), Y - 26, 30, -1e6, { dur: 0.01, align: dir < 0 ? 'right' : 'left', color: PAL.red, seed: seed + 6 });
    }
  }

  /* speed gauge: paper dial, m/s ticks, red needle, "20.0 m/s" + "72 km/h" readouts.
     (x, y) = dial centre, r = radius px, v = speed m/s (sign ignored).
     opts: { max = 30, label = 'SPEED', kmhLine = true, seed = 9, big = false (only the m/s number, large) } */
  function drawGauge(x, y, r, v, o = {}) {
    const max = o.max ?? 30, seed = o.seed ?? 9, sp = Math.abs(v);
    const a0 = Math.PI * 0.8, a1 = Math.PI * 2.2;         // 144° … 396° (clockwise, canvas angles)
    const ang = u => a0 + (a1 - a0) * clamp_(u, 0, 1.04);
    paperCircle(x, y, r, PAL.cream, { seed, depth: 1.2 });
    paperCircle(x, y, r * 0.9, PAL.white, { seed: seed + 1, depth: 0 });
    // red zone near the top of the scale
    g.save(); g.beginPath(); g.arc(x, y, r * 0.8, ang(0.8), ang(1)); g.strokeStyle = 'rgba(224,83,61,0.35)'; g.lineWidth = r * 0.1; g.stroke(); g.restore();
    g.beginPath();
    const stepV = max <= 30 ? 1 : 5, big = max <= 30 ? 5 : 10;
    for (let u = 0; u <= max + 1e-6; u += stepV) {
      const a = ang(u / max), L = u % big === 0 ? 0.16 : 0.08;
      g.moveTo(x + Math.cos(a) * r * 0.86, y + Math.sin(a) * r * 0.86); g.lineTo(x + Math.cos(a) * r * (0.86 - L), y + Math.sin(a) * r * (0.86 - L));
    }
    g.strokeStyle = PAL.ink; g.lineWidth = 2.4; g.stroke();
    g.save(); g.font = `${Math.round(r * 0.17)}px 'Caveat Brush'`; g.fillStyle = PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle';
    for (let u = 0; u <= max + 1e-6; u += big) { const a = ang(u / max); g.fillText(String(u), x + Math.cos(a) * r * 0.57, y + Math.sin(a) * r * 0.57); }
    g.restore();
    hand(o.label ?? 'SPEED', x, y + r * 0.34, r * 0.16, -1e6, { dur: 0.01, color: PAL.navy, seed: seed + 2, font: "'Special Elite'" });
    // needle (paper sliver) + hub
    const a = ang(sp / max);
    paperPiece([[-r * 0.1, -r * 0.035], [r * 0.78, 0], [-r * 0.1, r * 0.035]], x, y, PAL.red, { rot: a, seed: seed + 3, depth: 0.8 });
    paperCircle(x, y, r * 0.08, PAL.ink, { seed: seed + 4, depth: 0.4 });
    // readouts under the dial
    const rw = r * 1.7;
    paperRect(x, y + r * 1.18, rw, r * 0.46, PAL.navy, { seed: seed + 5, depth: 1 });
    g.save(); g.font = `${Math.round(r * 0.3)}px 'Luckiest Guy'`; g.fillStyle = PAL.cream; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(sp.toFixed(1) + ' m/s', x, y + r * 1.2); g.restore();
    if (o.kmhLine !== false) hand(Math.round(kmh(sp)) + ' km/h', x, y + r * 1.62, r * 0.26, -1e6, { dur: 0.01, color: PAL.ink, seed: seed + 6 });
  }

  /* energy chart: PE (mustard), KE (red), HEAT (teal) bars + a TOTAL column that stacks all
     three inside a dashed outline at the reference total — a cheat makes the stack overflow
     or fall short of the outline.
     (x, y) = top-left, w × h px.  e = sample (or {PE, KE, heat, total}).
     opts: { ref = e.total0 ?? e.total (the dashed outline), max = ref × 1.3, show = {PE, KE, heat, total} (all true),
             values = true, title = 'ENERGY', seed = 12, s = 1 (pop scale), circleTotal = 0..1 (ink circle prog) } */
  function drawChart(x, y, w, h, e, o = {}) {
    if ((o.s ?? 1) <= 0) return;
    const seed = o.seed ?? 12, ref = o.ref ?? e.total0 ?? e.total, max = o.max ?? Math.max(ref * 1.3, e.total * 1.05, 1);
    const show = Object.assign({ PE: true, KE: true, heat: true, total: true }, o.show || {});
    g.save(); g.translate(x + w / 2, y + h / 2); g.scale(o.s ?? 1, o.s ?? 1); g.translate(-w / 2, -h / 2);
    if (IMG.tex_graph) texScrap('tex_graph', w / 2, h / 2, w, h, { seed, rot: -0.01, depth: 1.2, zoom: 2.2, torn: false });
    else paperRect(w / 2, h / 2, w, h, PAL.white, { seed, depth: 1.2 });
    tape(w * 0.5, 4, 120, -0.05, undefined, seed + 1);
    hand(o.title ?? 'ENERGY', w / 2, 40, 40, -1e6, { dur: 0.01, color: PAL.navy, seed: seed + 2, font: "'Special Elite'" });
    const cols = [['PE', PAL.mustard, e.PE], ['KE', PAL.red, e.KE], ['HEAT', PAL.teal, e.heat]].filter(c => show[c[0] === 'HEAT' ? 'heat' : c[0]]);
    const n = cols.length + (show.total ? 1 : 0), top = 108, base = h - 64, H = base - top;
    const slot = (w - 40) / n, bw = Math.min(90, slot * 0.62);
    const hOf = v => Math.max(0, v) / max * H;
    const lsz = Math.min(30, slot * 0.3), vsz = Math.min(24, slot * 0.25);
    const label = (txt, cx, color) => hand(txt, cx, base + 30, lsz, -1e6, { dur: 0.01, color, seed: seed + 5, font: "'Luckiest Guy'" });
    const D = displayJ(e);
    const val = (v, cx, yTop) => { if (o.values !== false) hand(fmtJ(v, D.unit), cx, yTop - 16, vsz, -1e6, { dur: 0.01, color: PAL.ink, seed: seed + 6 }); };
    ink([[14, base], [w - 14, base]], { w: 3, seed: seed + 3, step: 30 });
    cols.forEach((c, i) => {
      const cx = 20 + slot * (i + 0.5), bh = hOf(c[2]);
      if (bh > 1) paperRect(cx, base - bh / 2, bw, bh, c[1], { seed: seed + 10 + i, depth: 0.7, boil: false });
      label(c[0], cx, c[1] === PAL.mustard ? '#b07a12' : c[1]);
      val(D[c[0] === 'HEAT' ? 'heat' : c[0]], cx, base - bh);
    });
    if (show.total) {
      const cx = 20 + slot * (n - 0.5), rh = hOf(ref);
      let yb = base;
      [[e.PE, PAL.mustard], [e.KE, PAL.red], [e.heat, PAL.teal]].forEach(([v, col], i) => {
        const bh = hOf(v); if (bh < 0.5) return;
        paperRect(cx, yb - bh / 2, bw, bh, col, { seed: seed + 20 + i, depth: 0.4, boil: false }); yb -= bh;
      });
      // the dashed outline = the energy the cart started with
      g.save(); g.setLineDash([12, 8]); g.lineDashOffset = -(BF % 4) * 5; g.strokeStyle = PAL.ink; g.lineWidth = 4;
      g.strokeRect(cx - bw / 2 - 6, base - rh, bw + 12, rh); g.restore();
      label('TOTAL', cx, PAL.ink);
      val(D.total, cx, Math.min(base - rh, yb));
      if (o.circleTotal) inkCircle(cx, base - rh / 2, Math.max(bw, rh) * 0.62, { sx: 0.75, w: 5, color: PAL.red, seed: seed + 30, prog: o.circleTotal });
    }
    g.restore();
  }

  const Coaster = { G, Track, Sim, view, kmh, fmtJ, unitJ, displayJ, CART_FIT, drawTrack, drawCart, drawRuler, drawGauge, drawChart };
  if (typeof module !== 'undefined' && module.exports) module.exports = Coaster;
  if (typeof window !== 'undefined') window.Coaster = Coaster;
})(this);
